import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { InputNode } from '../types';

type NumberInputNodeProps = {
  node: InputNode;
  value: string | undefined;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
  error?: string;
};

/**
 * V1 number-input parity (RichTextEditor/nodes/number-input/{render,hooks}.ts)：
 *
 * 输入行为：
 *   - 只允许 0-9 + 单小数点 + 最多 2 位小数（正则 [^\d.], 不允许负号）
 *   - 越界自动 clamp 到 [min, max]（不是 block 丢弃）
 *   - 0 但 min > 0 时清空
 *   - 正在输入小数态（末尾 . 或 .x 一位）不 clamp,允许用户继续输入
 *   - onKeyDown 拦截非数字/控制键
 *
 * 渲染结构：
 *   <span class="exclusion-wrapper required-upload-group" data-group=...>  ← required 才有
 *     <span class="rte-text-required"><i icon-bitian/></span>             ← required 才有
 *     <span class="rte-number-input-wrap" data-placeholder=... data-has-value=...>
 *       <span aria-hidden mirror>{placeholder/value}</span>                 ← 测 input width
 *       <input class="rte-number-input" type="text" inputMode="numeric"
 *              min max placeholder style={width} />
 *     </span>
 *   </span>
 */
export const NumberInputNode: React.FC<NumberInputNodeProps> = React.memo(
  ({ node, value, onChange, disabled, error }) => {
    const initial =
      value === undefined && typeof node.defaultNumber === 'number'
        ? String(node.defaultNumber)
        : value ?? '';
    const hasValue = initial !== '';

    // V1 hooks.ts 严格输入逻辑
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        // 只保留数字和小数点（不允许负号 — 与 V1 一致）
        let raw = e.target.value.replace(/[^\d.]/g, '');
        const dotIdx = raw.indexOf('.');
        if (dotIdx !== -1) {
          // 只保留第一个小数点
          raw = raw.slice(0, dotIdx + 1) + raw.slice(dotIdx + 1).replace(/\./g, '');
          // 最多两位小数
          const updatedDot = raw.indexOf('.');
          if (raw.length - updatedDot - 1 > 2) {
            raw = raw.slice(0, updatedDot + 3);
          }
        }

        const finalDotIdx = raw.indexOf('.');
        const decimalDigits = finalDotIdx !== -1 ? raw.length - finalDotIdx - 1 : 0;
        // 正在输入小数态（末尾 . 或 .x 一位但未到 2 位）不 clamp,让用户继续输入
        const isTypingDecimal =
          raw.endsWith('.') || (finalDotIdx !== -1 && decimalDigits === 1);

        if (raw === '') {
          onChange(node.key!, '');
          return;
        }
        if (isTypingDecimal) {
          onChange(node.key!, raw);
          return;
        }

        const parsed = parseFloat(raw);
        if (!Number.isFinite(parsed)) {
          onChange(node.key!, '');
          return;
        }
        const min = typeof node.min === 'number' ? node.min : 0;
        const max = typeof node.max === 'number' ? node.max : Infinity;
        // V1 特殊行为：0 但 min > 0 时清空（不 clamp 到 min,而是清空让用户重输）
        if (parsed === 0 && 0 < min) {
          onChange(node.key!, '');
          return;
        }
        const clamped = Math.min(Math.max(parsed, min), max);
        // 保留用户输入的小数位数（不多于 2 位）
        const next = decimalDigits > 0 ? clamped.toFixed(decimalDigits) : String(clamped);
        onChange(node.key!, next);
      },
      [node.key, node.min, node.max, onChange],
    );

    // V1 hooks.ts keydown 拦截：允许控制键 + 数字 + 小数点,其他 preventDefault
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        const key = e.key;
        if (
          key === 'Backspace' ||
          key === 'Delete' ||
          key === 'Tab' ||
          key === 'ArrowLeft' ||
          key === 'ArrowRight' ||
          key === 'ArrowUp' ||
          key === 'ArrowDown' ||
          key === 'Home' ||
          key === 'End' ||
          key === 'Enter' ||
          e.ctrlKey ||
          e.metaKey
        ) {
          return;
        }
        if (!/^[\d.]$/.test(key)) {
          e.preventDefault();
        }
      },
      [],
    );

    // V1 hooks.ts calculateWidth：mirror span 实测 placeholder/value 渲染宽,
    // 再 max(24, min(600, width + 2)) clamp。
    const mirrorRef = useRef<HTMLSpanElement>(null);
    const measureFor = initial || node.placeholder || '0';
    const [measuredWidth, setMeasuredWidth] = useState(0);
    useLayoutEffect(() => {
      if (mirrorRef.current) {
        setMeasuredWidth(mirrorRef.current.offsetWidth);
      }
    }, [measureFor]);
    const inputWidth = Math.max(24, Math.min(600, measuredWidth + 2));

    const wrapEl = (
      <span
        className="rte-number-input-wrap"
        data-placeholder={node.placeholder ?? ''}
        data-has-value={hasValue ? '1' : '0'}
        data-group={node.exclusionGroup}
      >
        {/* hidden mirror 测 measureFor 实际渲染宽,absolute 不占布局 */}
        <span
          ref={mirrorRef}
          aria-hidden="true"
          style={{
            position: 'absolute',
            visibility: 'hidden',
            whiteSpace: 'nowrap',
            font: 'inherit',
            pointerEvents: 'none',
            top: 0,
            left: 0,
            padding: '0 6px',
          }}
        >
          {measureFor}
        </span>
        <input
          className={`rte-number-input ${error ? 'rte-number-error' : ''}`}
          data-type="number-input"
          data-key={node.key}
          type="text"
          inputMode="numeric"
          value={initial}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={node.placeholder}
          min={node.min}
          max={node.max}
          style={{ width: inputWidth }}
        />
      </span>
    );

    if (!node.required) return wrapEl;

    return (
      <span
        className="exclusion-wrapper required-upload-group"
        data-group={node.exclusionGroup}
      >
        <span className="rte-text-required">
          <i className="iconfont icon-bitian" style={{ fontSize: 10 }} />
        </span>
        {wrapEl}
      </span>
    );
  },
);
NumberInputNode.displayName = 'NumberInputNode';
